import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { fade, makeStyles } from "@material-ui/core/styles";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import Battery20Icon from "@material-ui/icons/Battery20";
import Battery50Icon from "@material-ui/icons/Battery50";
import Battery80Icon from "@material-ui/icons/Battery80";
import Battery90Icon from "@material-ui/icons/Battery90";
import BatteryAlertIcon from "@material-ui/icons/BatteryAlert";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import FlashOffIcon from "@material-ui/icons/FlashOff";
import SyncProblemIcon from "@material-ui/icons/SyncProblem";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    minWidth: 0,
  },
  contentStack: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
  },
  hero: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  heroTitle: {
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  heroActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  filtersPaper: {
    width: "100%",
    minWidth: 0,
    padding: theme.spacing(1.5),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  filtersHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  filtersTitle: {
    fontWeight: 600,
  },
  filtersActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "stretch",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
    [theme.breakpoints.down("lg")]: {
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  summaryCard: {
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 16,
    minHeight: 92,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: `1px solid ${fade(theme.palette.divider, 0.8)}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  summaryLabel: {
    fontSize: "0.74rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.7,
  },
  summaryValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    lineHeight: 1.1,
    marginTop: theme.spacing(0.75),
  },
  chip: {
    fontWeight: 600,
  },
  issueText: {
    display: "block",
    marginTop: theme.spacing(0.75),
  },
  tablePaper: {
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  tableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  tableHeaderTitle: {
    fontWeight: 600,
  },
  tableContainer: {
    width: "100%",
    minWidth: 0,
    maxHeight: "68vh",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  connectionCellTitle: {
    fontWeight: 600,
  },
  connectionCellSubtext: {
    opacity: 0.72,
  },
  emptyState: {
    padding: theme.spacing(5, 2),
    textAlign: "center",
  },
  lineCritical: {
    backgroundColor: fade(theme.palette.error.main, 0.08),
  },
  lineHigh: {
    backgroundColor: fade(theme.palette.warning.main, 0.1),
  },
  lineMedium: {
    backgroundColor: fade(theme.palette.warning.light, 0.12),
  },
  nowrap: {
    whiteSpace: "nowrap",
  },
}));

const CHANNEL_OPTIONS = ["whatsapp", "facebook", "instagram"];
const STATUS_OPTIONS = ["CONNECTED", "DISCONNECTED", "qrcode", "OPENING", "PENDING"];
const COMPANY_STATUS_OPTIONS = ["active", "inactive"];

const SUMMARY_CARD_CONFIG = [
  { key: "totalConnections", label: "Total de conexoes" },
  { key: "totalCompanies", label: "Empresas no recorte" },
  { key: "connected", label: "Conectadas" },
  { key: "disconnected", label: "Desconectadas" },
  { key: "awaitingQrCode", label: "Aguardando QR" },
  { key: "withSyncIssues", label: "Com problema de sync" },
];

const INITIAL_SUMMARY = {
  totalConnections: 0,
  totalCompanies: 0,
  connected: 0,
  disconnected: 0,
  awaitingQrCode: 0,
  withSyncIssues: 0,
};

const getStatusMeta = (status) => {
  switch (status) {
    case "CONNECTED":
      return {
        label: "Conectada",
        style: { backgroundColor: "#dcfce7", color: "#166534" },
      };
    case "DISCONNECTED":
      return {
        label: "Desconectada",
        style: { backgroundColor: "#fee2e2", color: "#991b1b" },
      };
    case "qrcode":
      return {
        label: "Aguardando QR",
        style: { backgroundColor: "#fef3c7", color: "#92400e" },
      };
    case "OPENING":
      return {
        label: "Iniciando",
        style: { backgroundColor: "#dbeafe", color: "#1d4ed8" },
      };
    case "PENDING":
      return {
        label: "Pendente",
        style: { backgroundColor: "#ffedd5", color: "#c2410c" },
      };
    default:
      return {
        label: status || "Em analise",
        style: { backgroundColor: "#e5e7eb", color: "#374151" },
      };
  }
};

const getSyncMeta = (sync) => {
  const mode = sync?.mode;

  switch (mode) {
    case "idle":
      return {
        label: "Sem pendencias",
        style: { backgroundColor: "#dcfce7", color: "#166534" },
      };
    case "collecting":
      return {
        label: "Coletando historico",
        style: { backgroundColor: "#dbeafe", color: "#1d4ed8" },
      };
    case "running":
      return {
        label: "Importando",
        style: { backgroundColor: "#dbeafe", color: "#1e3a8a" },
      };
    case "pending":
      return {
        label: "Aguardando inicio",
        style: { backgroundColor: "#fef3c7", color: "#92400e" },
      };
    case "awaiting_ticket_closure":
      return {
        label: "Aguardando fechamento",
        style: { backgroundColor: "#ffedd5", color: "#c2410c" },
      };
    case "not_applicable":
    default:
      return {
        label: "Nao se aplica",
        style: { backgroundColor: "#e5e7eb", color: "#374151" },
      };
  }
};

const getCompanyMeta = (status) => {
  if (status) {
    return {
      label: "Empresa ativa",
      style: { backgroundColor: "#dcfce7", color: "#166534" },
    };
  }

  return {
    label: "Empresa inativa",
    style: { backgroundColor: "#e5e7eb", color: "#374151" },
  };
};

const getSeverity = (connection) => {
  if (connection?.sync?.hasIssue && connection?.status === "DISCONNECTED") {
    return "critical";
  }

  if (connection?.sync?.hasIssue) {
    return "high";
  }

  if (connection?.status === "qrcode") {
    return "medium";
  }

  if (connection?.status === "OPENING" || connection?.status === "PENDING") {
    return "medium";
  }

  return "normal";
};

const compareConnections = (left, right) => {
  const severityWeight = {
    critical: 0,
    high: 1,
    medium: 2,
    normal: 3,
  };

  const severityDiff =
    severityWeight[getSeverity(left)] - severityWeight[getSeverity(right)];

  if (severityDiff !== 0) {
    return severityDiff;
  }

  if (left.status !== "CONNECTED" && right.status === "CONNECTED") {
    return -1;
  }

  if (left.status === "CONNECTED" && right.status !== "CONNECTED") {
    return 1;
  }

  const companyDiff = (left.company?.name || "").localeCompare(
    right.company?.name || "",
    "pt-BR"
  );

  if (companyDiff !== 0) {
    return companyDiff;
  }

  return (left.name || "").localeCompare(right.name || "", "pt-BR");
};

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }

  try {
    return format(parseISO(value), "dd/MM/yy HH:mm");
  } catch (error) {
    return "-";
  }
};

const formatBattery = (battery) => {
  if (battery === null || battery === undefined || battery === "") {
    return "-";
  }

  const normalized = Number(battery);
  if (Number.isNaN(normalized)) {
    return String(battery);
  }

  return `${normalized}%`;
};

const BatteryIcon = ({ battery }) => {
  const normalized = Number(battery);

  if (Number.isNaN(normalized)) {
    return <BatteryAlertIcon fontSize="small" />;
  }

  if (normalized >= 90) {
    return <Battery90Icon fontSize="small" />;
  }

  if (normalized >= 75) {
    return <Battery80Icon fontSize="small" />;
  }

  if (normalized >= 45) {
    return <Battery50Icon fontSize="small" />;
  }

  if (normalized >= 20) {
    return <Battery20Icon fontSize="small" />;
  }

  return <BatteryAlertIcon fontSize="small" color="error" />;
};

const SuperConnectionsPanel = () => {
  const classes = useStyles();
  const [filters, setFilters] = useState({
    searchParam: "",
    channel: "",
    status: "",
    companyStatus: "",
    onlyProblems: false,
  });
  const [connections, setConnections] = useState([]);
  const [summary, setSummary] = useState(INITIAL_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchConnections = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const params = {};

        if (filters.searchParam.trim()) {
          params.searchParam = filters.searchParam.trim();
        }

        if (filters.channel) {
          params.channel = filters.channel;
        }

        if (filters.status) {
          params.status = filters.status;
        }

        if (filters.companyStatus) {
          params.companyStatus = filters.companyStatus;
        }

        if (filters.onlyProblems) {
          params.onlyProblems = true;
        }

        const { data } = await api.get("/settings/super/connections", {
          params,
        });

        const normalizedConnections = Array.isArray(data?.connections)
          ? data.connections.slice().sort(compareConnections)
          : [];

        setConnections(normalizedConnections);
        setSummary(data?.summary || INITIAL_SUMMARY);
        return true;
      } catch (error) {
        toastError(error);
        setConnections([]);
        setSummary(INITIAL_SUMMARY);
        return false;
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [filters]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchConnections();
    }, 300);

    return () => clearTimeout(timer);
  }, [fetchConnections]);

  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        filters.searchParam.trim() ||
          filters.channel ||
          filters.status ||
          filters.companyStatus ||
          filters.onlyProblems
      ),
    [filters]
  );

  const handleFilterChange = (field) => (event) => {
    const value =
      field === "onlyProblems" ? event.target.checked : event.target.value;

    setFilters((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      searchParam: "",
      channel: "",
      status: "",
      companyStatus: "",
      onlyProblems: false,
    });
  };

  const handleManualRefresh = async () => {
    const success = await fetchConnections({ silent: true });

    if (success) {
      toast.success("Painel de conexoes atualizado.");
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.contentStack}>
        <Box className={classes.hero}>
          <Box>
            <Typography variant="h5" className={classes.heroTitle}>
              Monitoramento global de conexoes
            </Typography>
          </Box>

          <Box className={classes.heroActions}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AutorenewIcon />}
              onClick={handleManualRefresh}
              disabled={refreshing}
            >
              Atualizar
            </Button>
          </Box>
        </Box>

        <div className={classes.summaryGrid}>
          {SUMMARY_CARD_CONFIG.map((card) => (
            <Paper className={classes.summaryCard} elevation={0} key={card.key}>
              <Typography className={classes.summaryLabel}>{card.label}</Typography>
              <Typography className={classes.summaryValue}>
                {summary[card.key] || 0}
              </Typography>
            </Paper>
          ))}
        </div>

        <Paper className={classes.filtersPaper} elevation={0}>
          <Box className={classes.filtersHeader}>
            <Typography variant="body1" className={classes.filtersTitle}>
              Filtros
            </Typography>
            <Box className={classes.filtersActions}>
              {hasActiveFilters ? <Chip size="small" label="Filtros ativos" /> : null}
              <Button size="small" onClick={handleClearFilters} disabled={!hasActiveFilters}>
                Limpar filtros
              </Button>
            </Box>
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Busca geral"
                placeholder="Empresa, conexao ou numero"
                value={filters.searchParam}
                onChange={handleFilterChange("searchParam")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                variant="outlined"
                size="small"
                label="Canal"
                value={filters.channel}
                onChange={handleFilterChange("channel")}
              >
                <MenuItem value="">Todos</MenuItem>
                {CHANNEL_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                variant="outlined"
                size="small"
                label="Status da conexao"
                value={filters.status}
                onChange={handleFilterChange("status")}
              >
                <MenuItem value="">Todos</MenuItem>
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {getStatusMeta(option).label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                variant="outlined"
                size="small"
                label="Status da empresa"
                value={filters.companyStatus}
                onChange={handleFilterChange("companyStatus")}
              >
                <MenuItem value="">Todas</MenuItem>
                {COMPANY_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option === "active" ? "Ativas" : "Inativas"}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={filters.onlyProblems}
                    onChange={handleFilterChange("onlyProblems")}
                  />
                }
                label="So problemas"
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper className={classes.tablePaper} elevation={0}>
          <Box className={classes.tableHeader}>
            <Typography variant="body1" className={classes.tableHeaderTitle}>
              Conexoes globais
            </Typography>
            <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
              <Chip size="small" label={`${connections.length} visiveis`} />
              <Chip size="small" label={`${summary.withSyncIssues || 0} com alerta`} />
            </Box>
          </Box>

          {(loading || refreshing) && <LinearProgress />}
          <TableContainer className={classes.tableContainer}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Empresa</TableCell>
                  <TableCell>Conexao</TableCell>
                  <TableCell>Canal</TableCell>
                  <TableCell>Numero</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Sync</TableCell>
                  <TableCell>Bateria</TableCell>
                  <TableCell>Energia</TableCell>
                  <TableCell>Padrao</TableCell>
                  <TableCell>Atualizado em</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading &&
                  connections.map((connection) => {
                    const statusMeta = getStatusMeta(connection.status);
                    const syncMeta = getSyncMeta(connection.sync);
                    const companyMeta = getCompanyMeta(connection.company?.status);
                    const severity = getSeverity(connection);

                    return (
                      <TableRow
                        key={connection.id}
                        className={
                          severity === "critical"
                            ? classes.lineCritical
                            : severity === "high"
                              ? classes.lineHigh
                              : severity === "medium"
                                ? classes.lineMedium
                                : ""
                        }
                      >
                        <TableCell>
                          <Typography className={classes.connectionCellTitle}>
                            {connection.company?.name || "-"}
                          </Typography>
                          <Chip
                            size="small"
                            label={companyMeta.label}
                            className={classes.chip}
                            style={companyMeta.style}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography className={classes.connectionCellTitle}>
                            {connection.name || "-"}
                          </Typography>
                          <Typography variant="body2" className={classes.connectionCellSubtext}>
                            {connection.isDefault ? "Conexao padrao" : "Conexao secundaria"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={connection.channel || "-"}
                            className={classes.chip}
                          />
                        </TableCell>
                        <TableCell className={classes.nowrap}>
                          {connection.number || "-"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={statusMeta.label}
                            className={classes.chip}
                            style={statusMeta.style}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={syncMeta.label}
                            className={classes.chip}
                            style={syncMeta.style}
                          />
                          {connection.sync?.issueLabel ? (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              className={classes.issueText}
                            >
                              <SyncProblemIcon
                                fontSize="inherit"
                                style={{ verticalAlign: "text-bottom", marginRight: 4 }}
                              />
                              {connection.sync.issueLabel}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {connection.channel === "whatsapp" ? (
                            <Box display="flex" alignItems="center" gridGap={6}>
                              <BatteryIcon battery={connection.battery} />
                              <span>{formatBattery(connection.battery)}</span>
                            </Box>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          {connection.channel === "whatsapp" ? (
                            <Box display="flex" alignItems="center" gridGap={6}>
                              {connection.plugged ? (
                                <FlashOnIcon fontSize="small" style={{ color: "#ca8a04" }} />
                              ) : (
                                <FlashOffIcon fontSize="small" color="disabled" />
                              )}
                              <span>{connection.plugged ? "Ligado" : "Desligado"}</span>
                            </Box>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>{connection.isDefault ? "Sim" : "-"}</TableCell>
                        <TableCell className={classes.nowrap}>
                          {formatDateTime(connection.updatedAt)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && connections.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography variant="h6">
                {hasActiveFilters
                  ? "Nenhuma conexao encontrada para os filtros aplicados"
                  : "Nenhuma conexao cadastrada"}
              </Typography>
            </Box>
          ) : null}
        </Paper>
      </div>
    </div>
  );
};

export default SuperConnectionsPanel;
